import { css, html, LitElement, PropertyValues } from "lit";
import { customElement } from "lit/decorators/custom-element.js";
import { sharedStyles } from "../../styles/shared";
import "../../atoms/card";
import { property } from "lit/decorators/property.js";
import { state } from "lit/decorators/state.js";
import { query } from "lit/decorators/query.js";
import { promisifyAnimation, waitForNextAnimationFrame } from "../../util/util";
import { when } from "lit/directives/when.js";
import { isVideo } from "../../atoms/section-split";
import { pickRandomItem, shuffleArray } from "shared";
import { FeatureCardItem } from "../../data/all-features";

@customElement("bs-landing-feature-carousel")
export class BsLandingFeatureCarousel extends LitElement {
	static get styles() {
		return [
			sharedStyles,
			css`
				:host {
					position: relative;
					display: flex;
					align-items: center;
					justify-content: center;
					width: var(--landing-feature-carousel-width, 100%);
					max-width: var(--landing-feature-carousel-max-width, 470px);
				}

				.card {
					width: 100%;
					height: 520px;
					position: absolute;
					z-index: 4;
					user-select: none;
				}

				#img,
				#video {
					height: 200px;
					object-fit: contain;
					max-width: 100%;
					width: 100%;
					border-radius: var(--border-radius-l);
					margin: var(--space-l) 0;
					background: var(--shade-light);
				}

				#label {
					text-transform: uppercase;
				}

				.card.empty:nth-child(1) {
					transform: scale(0.95) translateY(28px);
					z-index: 3;
				}

				.card.empty:nth-child(2) {
					transform: scale(0.9) translateY(56px);
					z-index: 2;
				}

				.card.empty:nth-child(3) {
					transform: scale(0.85) translateY(84px);
					z-index: 1;
				}
			`
		];
	}

	@property({ type: Array }) items: FeatureCardItem[] = [];

	@state() currentItem?: FeatureCardItem;
	@state() nextItem?: FeatureCardItem;

	@query("#current") $currentCard?: HTMLElement;
	@query("#next") $nextCard?: HTMLElement;

	private animating = false;
	private currentInterval?: number;

	protected firstUpdated(props: PropertyValues) {
		super.firstUpdated(props);
		this.shuffleItems();
		this.startShowNextItemInterval();
		this.showNextItem();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		if (this.currentInterval != null) {
			window.clearInterval(this.currentInterval);
		}
	}

	shuffleItems() {
		this.items = shuffleArray(this.items);
	}

	startShowNextItemInterval() {
		this.currentInterval = window.setInterval(() => this.showNextItem(), 7000);
	}

	async showNextItem() {
		if (this.animating) return;
		this.animating = true;

		this.nextItem = pickRandomItem(
			this.items.filter((item) => !item.hidden && !item.data?.soon && item.id !== this.currentItem?.id)
		);
		await waitForNextAnimationFrame();

		if (this.$currentCard != null && this.$nextCard != null) {
			// Animate current away and next in
			const currentAnimation = this.$currentCard.animate(
				{
					opacity: [1, 0],
					transform: ["translateY(0) scale(1)", "translateY(-28px) scale(1.05)"]
				},
				{
					duration: 500,
					easing: "ease-out"
				}
			);

			const nextAnimation = this.$nextCard.animate(
				{
					opacity: [0, 1],
					transform: ["translateY(28px) scale(0.95)", "translateY(0) scale(1)"]
				},
				{
					duration: 500,
					easing: "ease-out"
				}
			);

			await Promise.all([promisifyAnimation(currentAnimation), promisifyAnimation(nextAnimation)]);
		}

		this.currentItem = this.nextItem;
		this.nextItem = undefined;
		this.animating = false;
	}

	renderRenderFeatureCard({ id, item }: { id: string; item: FeatureCardItem }) {
		const { name } = item;
		const { illustration, text } = item.data;
		const illustrationSrc = `/assets/landing/features/${illustration}`;
		return html`
			<bs-card id="${id}" class="card" size="large" @click="${this.showNextItem}">
				<article>
					<bs-text id="label" label>Feature highlight</bs-text>
					${when(
						isVideo(illustrationSrc),
						() =>
							html`<video id="video" autoplay loop muted playsinline alt="Illustration" src="${illustrationSrc}" />`,
						() => html` <img id="img" alt="Illustration" src="${illustrationSrc}" loading="lazy" /> `
					)}
					<bs-title size="large" margin>${name}</bs-title>
					<bs-text size="large">${text}</bs-text>
				</article>
			</bs-card>
		`;
	}

	renderEmptyCard() {
		return html` <bs-card class="card empty"></bs-card> `;
	}

	render() {
		return html`
			${this.renderEmptyCard()} ${this.renderEmptyCard()} ${this.renderEmptyCard()}
			${this.currentItem != null
				? html`${this.renderRenderFeatureCard({ id: "current", item: this.currentItem })}`
				: html``}
			${this.nextItem != null ? html`${this.renderRenderFeatureCard({ id: "next", item: this.nextItem })}` : html``}
		`;
	}
}
